import { IdentityRole } from "core";
import { useMemo } from "react";
import { useAuthContext } from "../auth/useAuthContext";
import { trpc } from "./trpc";

export function useProjects() {
  const { data, isLoading, ...rest } =
    trpc.organization.getAll.useQuery(undefined);

  return {
    projects: data ?? [],
    isLoadingProjects: isLoading,
    ...rest,
  };
}

function useProjectById(id: string = "") {
  const { user } = useAuthContext();
  const { data, ...rest } = trpc.organization.getById.useQuery(
    { id },
    { enabled: !!id },
  );

  return {
    project: data
      ? {
          ...data,
          currentUserIdentity: data.identities.find(
            (i) => i.authUserId === user.id,
          ),
          members: data.identities.filter(
            (identity) => identity.primaryRole !== IdentityRole.Client,
          ),
          clients: data.identities.filter(
            (identity) => identity.primaryRole === IdentityRole.Client,
          ),
        }
      : undefined,
    ...rest,
  };
}

export function useProject() {
  const { identity } = useAuthContext();
  const { project, isLoading, ...proj } = useProjectById(
    identity?.organizationId,
  );

  return {
    ...proj,
    isLoadingProject: isLoading,
    project,
  };
}

export function useCreateProject() {
  const utils = trpc.useUtils();

  const { mutate: createProject, isLoading: isCreatingProject } =
    trpc.organization.create.useMutation({
      onSettled() {
        utils.organization.getAll.invalidate();
      },
    });

  return {
    createProject,
    isCreatingProject,
  };
}

export function useUpdateProject() {
  const utils = trpc.useUtils();

  const {
    mutate: updateProject,
    isLoading: isUpdatingProject,
    status: updateProjectStatus,
  } = trpc.organization.update.useMutation({
    onSettled() {
      utils.organization.getById.invalidate();
      utils.organization.getAll.invalidate();
    },
  });

  return {
    updateProject,
    updateProjectStatus,
    isUpdatingProject,
  };
}

export function useSwitchProject() {
  const { isLoading: isSwitchingProject, ...mutation } =
    trpc.organization.switch.useMutation();

  function switchProject({
    id,
    onSuccess,
  }: {
    id: string;
    onSuccess?: () => void;
  }) {
    mutation.mutate(
      { id },
      {
        onSuccess() {
          onSuccess?.();
        },
        onSettled() {
          location.reload();
        },
      },
    );
  }

  return {
    switchProject,
    isSwitchingProject,
  };
}

export function useProjectStacks() {
  const { project, isLoadingProject, isRefetching } = useProject();

  const projectStacks = useMemo(
    () => project?.settings.stacks || [],
    [project?.settings.stacks],
  );

  return {
    projectStacks,
    isLoadingProjectStacks: isLoadingProject,
    isRefetchingProjectStacks: isRefetching,
  };
}

export function useUpdateStacks() {
  const utils = trpc.useUtils();

  const { mutate: updateStacks, isLoading: isUpdatingStacks } =
    trpc.organization.updateStacks.useMutation({
      onSettled() {
        utils.organization.getById.invalidate();
      },
    });

  return {
    updateStacks,
    isUpdatingStacks,
  };
}

export function useLogoUpload() {
  const utils = trpc.useUtils();

  const {
    data: logoUploadURL,
    mutate: createLogoUploadURL,
    isLoading: isCreatingLogoUploadURL,
    isError: isLogoUploadURLError,
  } = trpc.organization.createLogoUploadURL.useMutation();

  const {
    mutate: commitLogo,
    isLoading: isCommittingLogo,
    isError: isCommitLogoError,
    data: committedLogo,
  } = trpc.organization.commitLogo.useMutation({
    onSettled() {
      utils.organization.getById.invalidate();
      utils.organization.getAll.invalidate();
    },
  });

  return {
    logoUploadURL,
    createLogoUploadURL,
    isCreatingLogoUploadURL,
    isLogoUploadURLError,

    commitLogo,
    isCommittingLogo,
    isCommitLogoError,
    committedLogo,
  };
}
export function useDeleteLogo() {
  const utils = trpc.useUtils();

  const { mutate: deleteLogo, isLoading: isDeletingLogo } =
    trpc.organization.deleteLogo.useMutation({
      onSettled() {
        utils.organization.getById.invalidate();
      },
    });

  return {
    deleteLogo,
    isDeletingLogo,
  };
}

export function useClaimCoupon() {
  const utils = trpc.useUtils();

  return trpc.organization.claimCoupon.useMutation({
    onSettled() {
      utils.organization.getById.invalidate();
    },
  });
}
